import { useCallback, useMemo, useRef, useState } from 'react';
import { UploadQueue, type UploadJob, type UploadJobStatus } from '@/lib/uploadQueue';

export interface UseUploadQueueOptions<T, R = unknown> {
  concurrency?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  processor: (data: T, signal: AbortSignal) => Promise<R>;
}

export interface UploadQueueStats {
  total: number;
  queued: number;
  uploading: number;
  retrying: number;
  done: number;
  failed: number;
  cancelled: number;
}

export function useUploadQueue<T, R = unknown>(
  opts: UseUploadQueueOptions<T, R>
) {
  const [version, setVersion] = useState(0);
  const queueRef = useRef<UploadQueue<T, R>>();

  if (!queueRef.current) {
    queueRef.current = new UploadQueue<T, R>({
      ...opts,
      onChange: () => setVersion((v) => v + 1),
    });
  }

  const queue = queueRef.current;

  const jobs = useMemo(() => queue.getJobs(), [version, queue]);

  const stats = useMemo<UploadQueueStats>(() => {
    const s: UploadQueueStats = {
      total: jobs.length,
      queued: 0,
      uploading: 0,
      retrying: 0,
      done: 0,
      failed: 0,
      cancelled: 0,
    };
    for (const j of jobs) {
      s[j.status]++;
    }
    return s;
  }, [jobs]);

  const add = useCallback(
    (data: T): string => queue.add(data),
    [queue]
  );

  const retry = useCallback(
    (id: string): boolean => queue.retry(id),
    [queue]
  );

  const retryAll = useCallback(() => queue.retryAll(), [queue]);

  const cancel = useCallback(
    (id: string): boolean => queue.cancel(id),
    [queue]
  );

  const pause = useCallback(() => queue.pause(), [queue]);
  const resume = useCallback(() => queue.resume(), [queue]);
  const clearCompleted = useCallback(() => queue.clearCompleted(), [queue]);

  return {
    jobs,
    stats,
    add,
    retry,
    retryAll,
    cancel,
    pause,
    resume,
    isPaused: queue.isPaused(),
    clearCompleted,
  };
}
